import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Grid, Typography, Avatar, Paper, Divider, Chip, CircularProgress, Tabs, Tab, Dialog, DialogTitle, IconButton, DialogContent } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import apiUrl from '../../Config';
import CloseIcon from '@mui/icons-material/Close';

const ViewTechnicianDetails = () => {
    const location = useLocation();
    const [technicians, setTechnicians] = useState([]);
    const navigate = useNavigate();
    const [error, setError] = useState(null);

    const technicianId = location?.state?.techinicianId;
    const [tabIndex, setTabIndex] = useState(0);
    const [serviceSubTab, setServiceSubTab] = useState(0);
    const [open, setOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const handleServiceSubTabChange = (event, newValue) => {
        setServiceSubTab(newValue);
    };
    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const getTechnicianDetails = async () => {
        try {
            const response = await axios.get(`${apiUrl}/technician/technician/${technicianId}`);
            if (response?.data?.status === true) {
                setTechnicians(response?.data?.technician);
            }
        } catch (error) {
            setError(error?.response?.data?.message || "Error fetching technician details");
        }
    };


    const handleOpen = (doc) => {
        setSelectedDoc(doc);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedDoc(null);
    };
    const isImageLike = (path) => /\.(jpg\d*|jpeg\d*|png\d*|gif\d*|webp\d*)$/i.test(path);
    const isPDF = (path) => /\.pdf$/i.test(path);

    useEffect(() => {
        getTechnicianDetails();
    }, [technicianId]);

    if (!technicians) {
        return (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                <CircularProgress />
                <Typography mt={2}>Loading showroom data...</Typography>
            </Box>
        );
    }


    const { profileImage, firstName, lastName, email, phone, experienceLevel,
        technicianType, isActive, isOnline, isVerified, isMobileVerified, latitude,
        longitude, overAllRating, totalWorkAssigned, completedAssignedWorks, cancelledAssignedWorks,
        skillServices, documents, registeredFrom, createdAt, updatedAt, skillPackageServices, bankAccount
    } = technicians;
    return (
        <>
            <Box sx={{ p: { xs: 2, sm: 4 } }}>
                {/* Header */}
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: "pointer" }} onClick={() => navigate(-1)}>
                        <ArrowLeft size={18} color="#333" />
                        <Typography variant="h6" sx={{ ml: 1, color: "#333" }}>
                            Back
                        </Typography>
                    </Box>
                    <Typography variant="h4" sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', color: "#333" }}>
                        Technician Details
                    </Typography>
                </Box>


                <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{ mb: 3 }}>
                    <Tab label="Profile" />
                    <Tab label="Status" />
                    <Tab label="Location" />
                    <Tab label="Work Details" />
                    <Tab label="Services" />

                    <Tab label="Documents" />
                    <Tab label="BankDetails" />
                    <Tab label="System Info" />
                </Tabs>

                <Grid container spacing={3}>
                    {/* Profile Avatar */}


                    {/* Personal Information */}
                    {tabIndex === 0 && (
                        <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                            <Typography variant="h6">Personal Information</Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, sm: 12, md: 12 }}>
                                    <Avatar src={profileImage} alt="Technician" sx={{ width: 100, height: 100 }} />
                                </Grid>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Name:</b> {firstName} {lastName}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Email:</b> {email}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Phone:</b> {phone}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Experience Level:</b> {experienceLevel}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Technician Type:</b> {technicianType}</Typography></Grid>
                            </Grid>
                        </Grid>)}

                    {/* Status Section */}
                    {tabIndex === 1 && (
                        <Grid item size={12}>
                            <Typography variant="h6">Status</Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Active:</b> {isActive ? 'Yes' : 'No'}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Online:</b> {isOnline ? 'Yes' : 'No'}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Verified:</b> {isVerified ? 'Yes' : 'No'}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Mobile Verified:</b> {isMobileVerified ? 'Yes' : 'No'}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Registered From:</b> {registeredFrom}</Typography></Grid>
                            </Grid>
                        </Grid>
                    )}

                    {/* Location */}
                    {tabIndex === 2 && (
                        <Grid item size={12}>
                            <Typography variant="h6">Geolocation</Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Latitude:</b> {latitude}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Longitude:</b> {longitude}</Typography></Grid>
                            </Grid>
                        </Grid>
                    )}

                    {/* Work Details */}
                    {tabIndex === 3 && (
                        <Grid item size={12}>
                            <Typography variant="h6">Work Details</Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, sm: 3 }}><Typography><b>Total Assigned:</b> {totalWorkAssigned}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 3 }}><Typography><b>Completed:</b> {completedAssignedWorks}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 3 }}><Typography><b>Cancelled:</b> {cancelledAssignedWorks}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 3 }}><Typography><b>Overall Rating:</b> {overAllRating}</Typography></Grid>
                            </Grid>
                        </Grid>)}

                    {/* Skills / Services */}
                    {tabIndex === 4 && (
                        <Grid item size={12}>
                            <Typography variant="h6">Services & Package Services</Typography>
                            <Divider sx={{ mb: 1 }} />

                            {/* Sub Tabs */}
                            <Tabs value={serviceSubTab} onChange={handleServiceSubTabChange} sx={{ mb: 2 }}>
                                <Tab label="Services" />
                                <Tab label="Package Services" />
                            </Tabs>

                            {/* Services Tab */}
                            {serviceSubTab === 0 && (
                                <>
                                    {skillServices?.length > 0 ? (
                                        <Grid container spacing={1}>
                                            {skillServices?.map((skill, index) => (
                                                <Grid item key={index}>
                                                    <Chip label={skill?.service?.name || `Skill ${index + 1}`} />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '100px' }}>
                                            <Grid item><p>No services found</p></Grid>
                                        </Grid>
                                    )}
                                </>
                            )}

                            {/* Package Services Tab */}
                            {serviceSubTab === 1 && (
                                <>
                                    {skillPackageServices?.length > 0 ? (
                                        <Grid container spacing={1}>
                                            {skillPackageServices?.map((skill, index) => (
                                                <Grid item key={index}>
                                                    <Chip label={skill?.packageService?.name || `Package ${index + 1}`} />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '100px' }}>
                                            <Grid item><p>No package services found</p></Grid>
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Grid>
                    )}


                    {/* Documents */}
                    {tabIndex === 5 && (
                        <Grid item size={12}>
                            <Typography variant="h6">Documents</Typography>
                            <Divider sx={{ mb: 1 }} />
                            {documents?.length > 0 ? (
                                <Grid container spacing={2}>
                                    {documents.map((doc) => (
                                        <Grid item size={{ xs: 12, md: 4, sm: 6 }} key={doc.id}>
                                            <Box sx={{ border: '1px solid #ccc', p: 2, borderRadius: 1 }}>
                                                <Typography variant="subtitle1" gutterBottom>
                                                    <strong>{doc.documentName}</strong>
                                                </Typography>

                                                {/* Thumbnail preview (optional) */}
                                                {isImageLike(doc.documentPath) ? (
                                                    <img
                                                        src={doc.documentPath}
                                                        alt={doc.documentName}
                                                        style={{
                                                            width: '100%',
                                                            height: '150px',
                                                            objectFit: 'cover',
                                                            borderRadius: 4,
                                                            marginBottom: '8px',
                                                        }}
                                                    />
                                                ) : isPDF(doc.documentPath) ? (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: '160px',
                                                            backgroundColor: '#f5f5f5',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            borderRadius: 2,
                                                            marginBottom: '8px',
                                                        }}
                                                    >
                                                        <Typography variant="body2">📄 PDF File</Typography>
                                                    </Box>
                                                ) : null}


                                                <Typography
                                                    onClick={() => handleOpen(doc)}
                                                    sx={{ color: 'blue', cursor: 'pointer', textDecoration: 'underline', mt: 1 }}
                                                >
                                                    View Document
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Grid container justifyContent="center" alignItems="center" style={{ minHeight: '200px' }}>
                                    <Grid item>
                                        <Typography>No documents found</Typography>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    )}

                    {/* Bank Details */}
                    {tabIndex === 6 && (
                        <Grid item size={12}>
                            <Typography variant="h6">BankDetails</Typography>
                            <Divider sx={{ mb: 1 }} />
                            {bankAccount ? (

                                <Grid container spacing={2}>
                                    <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Account Holder Name:</b> {bankAccount?.accountHolderName}</Typography></Grid>
                                    <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Account Number:</b> {bankAccount?.accountNumber}</Typography></Grid>
                                    <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Bank Name:</b> {bankAccount?.bankName}</Typography></Grid>
                                    <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>IFSCCode:</b> {bankAccount?.ifscCode}</Typography></Grid>
                                    <Grid item size={{ xs: 12, sm: 4 }}><Typography><b>Is Deleted:</b> {bankAccount?.isDeleted ? 'Yes' : 'No'}</Typography></Grid>
                                </Grid>
                            ) : (<Grid container justifyContent="center" alignItems="center" style={{ minHeight: '200px' }}>
                                <Grid item>
                                    <p>No  bank details found</p>
                                </Grid>
                            </Grid>)}
                        </Grid>
                    )}
                    {/* System Info */}
                    {tabIndex === 7 && (
                        <Grid item size={12}>
                            <Typography variant="h6">System Info</Typography>
                            <Divider sx={{ mb: 1 }} />
                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, sm: 6 }}><Typography><b>Regester date :</b> {createdAt}</Typography></Grid>
                                <Grid item size={{ xs: 12, sm: 6 }}><Typography><b>Updated At:</b> {updatedAt}</Typography></Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>


                {error && (
                    <Box mt={2}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                )}
            </Box>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    {selectedDoc?.documentName}
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedDoc && isImageLike(selectedDoc.documentPath) ? (
                        <img
                            src={selectedDoc.documentPath}
                            alt={selectedDoc.documentName}
                            style={{ width: '100%', borderRadius: 4 }}
                        />
                    ) : selectedDoc && isPDF(selectedDoc.documentPath) ? (
                        <iframe
                            src={selectedDoc.documentPath}
                            title={selectedDoc.documentName}
                            width="100%"
                            height="600px"
                            style={{ border: 'none' }}
                        />
                    ) : (
                        <Typography>Preview not available</Typography>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ViewTechnicianDetails;
